import { Injectable, computed, effect, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import {
  Subscription,
  SubscriptionService
} from '../services/subscription.service';
import { TenantContextService } from '../services/tenant-context.service';
import { TtlCachedStore } from './ttl-cached-store.base';

/**
 * Holds the currently selected BU's active subscription so multiple UI surfaces
 * (topbar Plan pill, billing dashboard, banners, guards) can share it without
 * each fetching independently. Keyed by businessUnitId — switching BU triggers a
 * refetch even within the TTL window, same as before.
 */
@Injectable({ providedIn: 'root' })
export class SubscriptionStore extends TtlCachedStore<Subscription | null, string> {
  private subs   = inject(SubscriptionService);
  private tenant = inject(TenantContextService);
  protected readonly ttlMs = 2 * 60 * 1000;

  readonly current = computed(() => this._data() ?? null);

  readonly status = computed(() => this.current()?.status ?? null);
  readonly planName = computed(() => this.current()?.planName ?? null);
  readonly planCode = computed(() => this.current()?.planCode ?? null);
  readonly periodEnd = computed(() => this.current()?.currentPeriodEnd ?? null);
  readonly gracePeriodEnd = computed(() => this.current()?.gracePeriodEndsAt ?? null);

  readonly daysRemaining = computed(() => {
    const end = this.current()?.currentPeriodEnd;
    if (!end) return null;
    const ms = new Date(end).getTime() - Date.now();
    return Math.ceil(ms / (1000 * 60 * 60 * 24));
  });

  readonly isExpired = computed(() => this.current()?.status === 'Expired');
  readonly isInGrace = computed(() => this.current()?.status === 'Grace');

  constructor() {
    super();
    effect(() => {
      const buId = this.tenant.selectedBusinessUnitId();
      if (!buId) {
        this.reset();
        return;
      }
      this.loadOnce(buId);
    });
  }

  /** Forces a refetch of the current BU's subscription, bypassing the TTL. */
  async refresh(): Promise<void> {
    const buId = this.tenant.getCurrentBusinessUnitId();
    if (!buId) return;
    this.invalidate();
    await this.loadOnce(buId);
  }

  protected async fetch(buId: string): Promise<Subscription | null | undefined> {
    const res = await firstValueFrom(this.subs.getCurrent(buId));
    if (!res.success) return undefined;
    return (res.data as Subscription) || null;
  }
}
